import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import * as Sentry from "@sentry/react";
import Index from "./pages/Index";
import About from "./pages/About";
import Guide from "./pages/Guide";
import Favorites from "./pages/Favorites";
import SessionHistory from "./pages/SessionHistory";
import NotFound from "./pages/NotFound";
import SentryTest from "./pages/SentryTest";
import { useEffect } from 'react';
import { ChatWootWidget } from '@/components/chat/ChatWootWidget';
import { loadRemoteConfig } from '@/utils/supabaseConfig';
import { identifyAnonymousUser } from '@/utils/userIdentification';
import { NavigationTracker } from '@/components/navigation/NavigationTracker';
import Help from '@/pages/Help';
import { AuthProvider } from '@/contexts/AuthContext';
import SignIn from '@/pages/auth/SignIn';
import SignUp from '@/pages/auth/SignUp';
import SignUpSuccess from '@/pages/auth/SignUpSuccess';
import ResetPassword from '@/pages/auth/ResetPassword';
import AuthCallback from '@/pages/auth/AuthCallback';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import MyProfile from '@/pages/profile/MyProfile';

// Initialize Sentry
Sentry.init({
  dsn: "https://d6b74f6cab23d60674b8caeeb52fac1d@o4509052012724224.ingest.us.sentry.io/4509052018163712"
});

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Load remote configuration and identify user on app initialization
    loadRemoteConfig().then(() => {
      identifyAnonymousUser();
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router>
            <AuthProvider>
              <NavigationTracker />
              <Routes>
                {/* Public routes */}
                <Route path="/auth/signin" element={<SignIn />} />
                <Route path="/auth/signup" element={<SignUp />} />
                <Route path="/auth/signup-success" element={<SignUpSuccess />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />

                {/* Protected routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                } />
                <Route path="/about" element={<About />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><SessionHistory /></ProtectedRoute>} />
                <Route path="/sentry-test" element={<SentryTest />} />
                <Route path="/privacy" element={<NotFound />} />
                <Route path="/terms" element={<NotFound />} />
                <Route path="/cookies" element={<NotFound />} />
                <Route path="/contact" element={<NotFound />} />
                <Route path="/help" element={<Help />} />
                <Route path="/profile" element={<MyProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </Router>
          <ChatWootWidget />
        </TooltipProvider>
      </AnalyticsProvider>
    </QueryClientProvider>
  );
};

export default App;
